import { Component, Input, OnChanges } from "@angular/core";
import { AgreementSigner, AgreementSignerStatus } from "common";

/** The signer list on the dashboard's Financing card. */
@Component({
  selector: "ifp-agreement-signers",
  templateUrl: "./agreement-signers.component.html",
  styleUrls: ["./agreement-signers.component.scss"],
  standalone: false,
})
export class AgreementSignersComponent implements OnChanges {
  @Input() signers: AgreementSigner[];

  /** Everything the template renders, precomputed — initials, the chip's label and its modifier class. */
  rows: AgreementSignerRow[] = [];

  ngOnChanges(): void {
    this.rows = (this.signers ?? []).map((signer) => ({
      customerId: signer.customerId,
      fullName: signer.fullName,
      initials: AgreementSignersComponent.initialsOf(signer.fullName),
      statusLabel: AgreementSignersComponent.labelOf(signer.signingStatus),
      statusModifier: signer.signingStatus,
    }));
  }

  trackByCustomerId(_index: number, row: AgreementSignerRow): number {
    return row.customerId;
  }

  /** First letter of the first and last words — "Maria Rodriguez" becomes MR. */
  private static initialsOf(fullName: string): string {
    const words = (fullName ?? "").trim().split(/\s+/).filter((word) => !!word);

    if (!words.length) {
      return "";
    }

    const first = words[0].charAt(0);
    const last = words.length > 1 ? words[words.length - 1].charAt(0) : "";

    return `${first}${last}`.toUpperCase();
  }

  private static labelOf(status: AgreementSignerStatus): string {
    switch (status) {
      case AgreementSignerStatus.Signed:
        return "Signed";
      case AgreementSignerStatus.ReadyToSign:
        return "Ready to sign";
      default:
        return "Pending";
    }
  }
}

/** One signer row, fully resolved for the template. */
export interface AgreementSignerRow {
  customerId: number;
  fullName: string;
  initials: string;
  statusLabel: string;
  /** Drives the chip's colour. The status value doubles as the CSS modifier. */
  statusModifier: AgreementSignerStatus;
}
